import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { UserCog, Lock, User as UserIcon, ArrowLeft } from "lucide-react";
import { staffApi, setStaffToken, getStaffToken } from "@/library/lib/staffApi";
import { toast } from "sonner";

export default function StaffLogin() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getStaffToken()) nav("/library/staff", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await staffApi.post("/auth/login", { username: username.trim(), password });
      setStaffToken(data.token);
      toast.success(`مرحباً ${data.user?.full_name || username}`);
      nav("/library/staff");
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل تسجيل الدخول");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="staff-login-page" className="min-h-screen flex items-center justify-center p-4 hero-gradient relative overflow-hidden">
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-gold/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-brand-light/30 rounded-full blur-3xl" />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-10"
      >
        <Link to="/library" data-testid="staff-login-back" className="text-xs text-slate-500 hover:text-brand font-medium flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3 h-3 rotate-180" /> العودة إلى بوابة المكتبة
        </Link>
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-dark to-gold flex items-center justify-center mb-5 shadow-lg">
          <UserCog className="w-8 h-8 text-white" />
        </div>
        <h1 className="font-heading text-2xl font-extrabold text-brand-dark">دخول منسوبي المدرسة</h1>
        <p className="text-sm text-slate-500 mt-1 mb-7">أدخل بيانات حسابك للوصول إلى مجلداتك وملفاتك الخاصة.</p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5">اسم المستخدم</label>
            <div className="relative">
              <UserIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="staff-login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
                required
                className="w-full pe-10 ps-4 py-3 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/15 outline-none text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5">كلمة المرور</label>
            <div className="relative">
              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="staff-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full pe-10 ps-4 py-3 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/15 outline-none text-sm"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          data-testid="staff-login-submit"
          disabled={loading}
          className="w-full mt-7 bg-gold hover:bg-gold-light text-white font-bold py-3 rounded-xl shadow-lg transition-all disabled:opacity-60"
        >
          {loading ? "جارٍ التحقق..." : "تسجيل الدخول"}
        </button>
      </motion.form>
    </div>
  );
}
